/* eslint-disable prettier/prettier */
import * as React from "react";
import {
  OutlinedInput,
  Box,
  Typography,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import Select from "@mui/material/Select";
import { Formik } from "formik";
import * as Yup from "yup";
import { useFeature } from "src/context/global";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import MenuItem from "@mui/material/MenuItem";
import Button from "src/components/shared/atoms/button";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import useStyle from "./Styles";

interface Props {
  addLabelHandler: (value: any) => void;
  updateLabelsQueryLoading: boolean;
  labelText: string;
  children?: React.ReactNode;
}

interface FormValues {
  label: number | string | null;
  type?: string;
}

const Input: React.FC<Props> = ({
  addLabelHandler,
  updateLabelsQueryLoading,
  labelText,
  children,
}) => {
  const { classes } = useStyle();
  const SUPERADMIN = "SUPERADMIN";
  const { panelType } = useActiveUserDetail();
  const [initialValues, setInitialValues] = React.useState({
    label: labelText,
    type: panelType === SUPERADMIN ? "" : "personal",
  });

  const { canUse } = useFeature();
  const canSavePersonalLabels = canUse("can_save_personal_labels");
  const canSaveAdminLabels = canUse("can_save_admin_labels");

  React.useEffect(() => {
    setInitialValues({
      label: labelText,
      type: panelType === SUPERADMIN ? "" : "personal",
    });
  }, [labelText]);

  const handleClick = async (values: FormValues, { setSubmitting }: any) => {
    setSubmitting(true);
    addLabelHandler(values);
    setSubmitting(false);
  };

  const validationSchema = Yup.object().shape({
    type: Yup.string().test(
      "required-if-superadmin",
      "Type of Label is required",
      function (value: string | undefined) {
        if (panelType === "SUPERADMIN") {
          return !!value;
        }
        return true;
      },
    ),
    label: Yup.string()
      .required("Label is required")
      .min(3, "Minimum 3 characters are required")
      .max(25, "Must not exceed 25 characters"),
  });

  return (
    <>
      <Box className={classes.inputContainer}>
        <Box className={classes.searchBox}>{children}</Box>
      </Box>

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleClick}
      >
        {(formik: any) => (
          <Box
            component="form"
            onSubmit={formik.handleSubmit}
            className={classes.formContainer}
          >
            {/* Label Field */}
            <Box className={classes.fieldWrapper}>
              <InputLabel className={classes.fieldText}>Label</InputLabel>
              <OutlinedInput
                type="text"
                placeholder="Add a Label"
                value={formik.values.label}
                name="label"
                id="label"
                onChange={formik.handleChange}
                className={classes.labelInput}
              />
              <FormHelperText error className={classes.errorStyle}>
                {formik.touched.label && formik.errors.label}
              </FormHelperText>
            </Box>

            {/* Type Select (SUPERADMIN only) */}
            {panelType === SUPERADMIN && (
              <Box className={classes.fieldWrapper}>
                <InputLabel className={classes.fieldText}>
                  Select Type
                </InputLabel>
                <Select
                  name="type"
                  value={formik.values.type}
                  className={classes.fieldStyle}
                  onChange={formik.handleChange}
                  displayEmpty
                  inputProps={{ "aria-label": "Without label" }}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                      {selected === "personal" && (
                        <>
                          <PersonIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="body2">My Response</Typography>
                        </>
                      )}
                      {selected === "generic" && (
                        <>
                          <GroupsIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            Generic Response
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}
                >
                  {canSavePersonalLabels && (
                    <MenuItem value="personal">
                      <PersonIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body2">My Response</Typography>
                    </MenuItem>
                  )}
                  {canSaveAdminLabels && (
                    <MenuItem value="generic">
                      <GroupsIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body2">Generic Response</Typography>
                    </MenuItem>
                  )}
                </Select>
                <FormHelperText error>
                  {formik.touched.type && formik.errors.type}
                </FormHelperText>
              </Box>
            )}

            {/* Submit Button */}
            <Box className={classes.addButtonContainer}>
              <Button
                variant="contained"
                className={classes.addButton}
                type="submit"
                circular={updateLabelsQueryLoading}
              >
                Create Label
              </Button>
            </Box>
          </Box>
        )}
      </Formik>
    </>
  );
};

export default Input;
